import { get, startCase } from "lodash";
import { patientRelationships } from "../slices/options/family";
import { identifierTypes } from "../slices/options/misc";
import { AppointmentParticipant } from "../types/appoitment/appointmentBase";
import { PatientContact } from "../types/patient/patientBase";
import { Reference } from "../types/reference";
import { MedicationStatement } from "../types/medicationStatement";
import { MedicationRequest } from "../types/medicationRequest";
import { CodeableConcept } from "../types/codeableConcept";
import { countriesArr } from "../slices/options/general";
import { Reference as ReferenceV5 } from "../types/v5/common";
import { Identifier } from "../types/identifier";

const patientRelationshipMap = patientRelationships.reduce((map, obj) => {
  // eslint-disable-next-line no-param-reassign
  map[obj.code] = obj.display;
  return map;
}, {});

export const generateAppointmentDescription = (
  participant: AppointmentParticipant[],
) => {
  if (Array.isArray(participant)) {
    return participant.map((p) => p.actor?.display.split("-")[0]).join(" · ");
  }

  return "";
};

export const generateHumanDisplayName = (humanName) => {
  if (!humanName) return "";
  if (Array.isArray(humanName)) {
    return generateHumanDisplayName(humanName[0]);
  }
  const { firstName, given, middleName, family, prefix, suffix } =
    humanName ?? {};
  humanName ?? {};

  const p1 = [
    prefix,
    given !== undefined && given !== "" ? given : firstName,
    middleName,
    family,
  ]
    .filter((e) => e && e !== "")
    .join(" ");
  const p2 = [suffix].filter((e) => e && e !== "").join(" ");

  const arr: string[] = [];
  if (p1 !== "") arr.push(p1);
  if (p2 !== "") arr.push(p2);

  return arr.join(", ");
};

export const generatePatientIdentifier = (identifier) => {
  if (!identifier) return "";

  const { type, value, use, assigner } = identifier ?? {};

  const typeVal = parseTextField(type);

  const typeDisplay = identifierTypes.find((i) => i.code === typeVal);

  return [
    use ? startCase(use) : undefined,
    typeDisplay ? startCase(typeDisplay.display) : type,
    assigner ? startCase(assigner.display || assigner) : undefined,
    value,
  ]
    .filter((e) => e && e !== "")
    .join(" · ");
};

export const getDerivedMedicationForm = (form) => {
  if (form) {
    const forms = form.split(",") ?? [];
    return forms[0].trim().toUpperCase();
  }
  return undefined;
};

export const getCountryFullName = (code?: string): string => {
  if (!code) return "";
  const codeLower = code.toLowerCase();

  const manualMapping: Record<string, string> = {
    atg: "Antigua and Barbuda",
    dma: "Dominica",
    lca: "Saint Lucia",
    tt: "Trinidad and Tobago",
  };

  if (manualMapping[codeLower]) {
    return manualMapping[codeLower];
  }

  const standardMatch = countriesArr.find(
    (c) => c.code.toLowerCase() === codeLower,
  );
  if (standardMatch) {
    return standardMatch.display;
  }

  return code;
};

export const generatePatientAddress = (address) => {
  if (!address) return "";
  const { line, city, state, country, postalCode, use } = address ?? {};
  const useVal = parseTextField(use);
  const countryName = getCountryFullName(country);
  return `${useVal ? `${startCase(useVal)}: ` : ""}${line ? `${line}` : ""}${
    city ? ` · ${city}` : ""
  }${state ? ` · ${state}` : ""}${countryName ? ` · ${countryName}` : ""}${
    postalCode ? ` · ${postalCode}` : ""
  }`;
};

export const renderAvailableTimes = (availability) => {
  const { daysOfWeek, availableStartTime, availableEndTime } =
    availability ?? {};

  // Hh:mm:ss to hh:mm format with removed seconds
  const convertTO12HrFormat = (time) =>
    time.split(":")[0] > 12
      ? `${Number(time.substring(0, 5).split(":")[0]) - 12}:${
          time.substring(0, 5).split(":")[1]
        } PM`
      : `${time.substring(0, 5)} AM`;

  return `${daysOfWeek.map((d) => startCase(d)).join(", ")}${
    availableStartTime ? `: ${convertTO12HrFormat(availableStartTime)}` : ""
  }${availableEndTime ? ` - ${convertTO12HrFormat(availableEndTime)}` : ""}`;
};

export const generateTelecomString = (telecom) => {
  const { value, use, system } = telecom ?? {};
  return `${use ? `${startCase(use)}` : ""}${
    system ? ` · ${startCase(system)}` : ""
  }${value ? ` · ${value}` : ""}`;
};

export const getOrgEmail = (org) => {
  const emailEntry = org?.telecom?.find((t) => t.system === "email");
  return emailEntry?.value || org?.email;
};

export const getOrgCountry = (org) =>
  org?.address?.[0]?.country || org?.country;

const getRelationshipDisplay = (
  relationship: CodeableConcept[] | CodeableConcept | string,
) => {
  if (!relationship) return "";

  if (Array.isArray(relationship)) {
    return relationship?.[0]?.coding?.[0]?.code
      ? `${patientRelationshipMap[relationship[0].coding[0].code]}${relationship[1]?.coding?.[0]?.code ? ` (${relationship[1].coding[0].display})` : ""}`
      : "";
  }
  if (typeof relationship === "string") {
    return patientRelationshipMap[relationship];
  }

  if (relationship.text) {
    return patientRelationshipMap[relationship.coding[0].code];
  }

  return "";
};

export const renderPatientMedicalContact = (
  contact: PatientContact,
): string => {
  const { name, relationship, telecom, organization } = contact ?? {};
  const relDisplay = relationship ? getRelationshipDisplay(relationship) : "";
  const isOrg = (() => {
    if (typeof relationship === "string") {
      return relationship === "I";
    }
    return relationship?.[0]?.coding?.[0]?.code === "I";
  })();
  const nameDisplay = isOrg
    ? (organization?.display ?? "")
    : generateHumanDisplayName(name?.[0] ?? "");
  const telecomDisplay = telecom
    ?.map((t) =>
      t?.value ? `${t.system ? startCase(t.system) : ""}: ${t.value}` : "",
    )
    .filter((part) => part.trim() !== ":") // Remove entries that result in just ":"
    .join(", ");
  return [relDisplay, nameDisplay, telecomDisplay].filter(Boolean).join(" - ");
};

export const createReference = (
  type: string,
  id: string,
  display?: string,
): Reference | null => {
  if (!id) return null;
  return {
    reference: `${type}/${id}`,
    type,
    identifier: id,
    display,
  };
};

const formatMedicationDisplayPart = (value: unknown): string | null => {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  return startCase(value.toLowerCase());
};

export const generateMedicationDisplay = (medication) => {
  const { medicineName, form, display } = medication ?? {};

  const parts = [
    formatMedicationDisplayPart(medicineName),
    formatMedicationDisplayPart(form),
  ].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(" · ");
  }

  // Reference / free-option rows often only have `display`
  return formatMedicationDisplayPart(display) ?? "";
};

// We might not need this function with the `getParticipantDisplayName` function introduced below
export const generateParticipantDisplay = (participants) => {
  if (participants && participants.length > 0) {
    const display = participants
      .map((p) => `${get(p, "actor.display")}`)
      .join(", ");

    return display;
  }
};

/**
 * Returns the display name of the participant based on the type of the participant
 * if the type is not provided, it will return the display name of the participant
 * @param participant
 * @param types
 * @returns
 * @throws Error
 * @example
 * getParticipantDisplayName(participant, ["Patient"])
 * getParticipantDisplayName(participant, ["Patient", "Practitioner"])
 * getParticipantDisplayName(participant)
 */
export const getParticipantDisplayName = (
  participant: AppointmentParticipant,
  types: string[] = [],
): string | null => {
  if (types.length === 0 || types.includes(participant.actor.type)) {
    if (participant.actor.type === "Patient") {
      return participant.actor.display;
    }
    if (participant.actor.type === "temp-Patient") {
      return `${participant.actor.value.name?.[0].firstName} ${
        participant.actor.value.name?.[0].family
      }`;
    }
    if (participant.actor.type === "Practitioner") {
      return `${participant.actor.display} `;
    }
  }
  return null;
};

export const getMedicationFrequencyPrescriptionDisplay = (
  display?: string,
  code?: string,
) => {
  if (!display && !code) return undefined;
  if (!display && code) return code;
  if (display && !code) return display;

  const displayWithoutNonAlphanumeric = display
    .replace(/\W/g, "")
    .toLowerCase();
  const codeWithoutNonAlphanumeric = code.replace(/\W/g, "").toLowerCase();

  if (displayWithoutNonAlphanumeric === codeWithoutNonAlphanumeric) {
    return startCase(display);
  }

  return code;
};

export const trimStringProperties = (obj) => {
  if (obj instanceof Date) {
    return obj;
  }
  // Check if the value is an object or array
  if (typeof obj === "object" && obj !== null) {
    // If it's an array, iterate over the elements
    if (Array.isArray(obj)) {
      return obj.map((element) => trimStringProperties(element));
    }
    // If it's an object, iterate over the properties
    const trimmedObj = {};
    for (const key in obj) {
      // Recursively trim each property
      trimmedObj[key] = trimStringProperties(obj[key]);
    }
    return trimmedObj;
  }
  if (typeof obj === "string") {
    // If it's a string, trim it
    return obj.trim();
  }
  // Return the value as is for other data types
  return obj;
};

export const createEncounterParam = (encounterReference) =>
  encounterReference?.reference
    ? { encounter: encounterReference.reference }
    : {};

export const createEncounterIdentifierParam = (encounterReference) =>
  encounterReference?.identifier
    ? { encounter: encounterReference.identifier }
    : {};

const parseTextField = (val: Record<string, unknown> | string): string => {
  if (typeof val === "string") {
    return val;
  }
  if (val?.display) {
    return val.display as string;
  }
  if (val?.text) {
    return val.text as string;
  }

  return "";
};

export const createNote = (
  value: string,
  practitionerReference: Reference,
) => ({
  text: value,
  time: new Date(),
  author: practitionerReference,
});

function DosageDisplay(dosage: any, asNeeded: string) {
  return dosage
    ?.map((d: any) => {
      const dose = get(d, "doseAndRate[0].dose.value") || "";
      const unit = get(d, "doseAndRate[0].dose.unit") || "";
      const timing = get(d, "timing.code.coding[0].code") || "";
      const route = get(d, "route.coding[0].display") || "";
      return [dose, unit, timing, asNeeded, route].filter(Boolean).join(" · ");
    })
    .join(", ");
}

export const getMedicationStatementDisplay = (
  statement: MedicationStatement,
): string => {
  const { status, medication, dosage } = statement ?? {};
  const asNeeded = dosage?.at(0)?.asNeeded?.asNeededBoolean
    ? "As Needed"
    : undefined;
  return [
    startCase(status),
    medication?.display,
    DosageDisplay(dosage, asNeeded),
  ]
    .filter((e) => e)
    .join(" · ");
};

export const getMedicationRequestDisplay = (
  request: MedicationRequest,
): string => {
  const { status, medication, dosageInstruction } = request ?? {};
  const { value, unit } =
    dosageInstruction?.at(0)?.doseAndRate?.at(0)?.dose ?? {};
  const timing =
    dosageInstruction?.at(0)?.timing?.code?.coding?.at(0)?.code ?? "";
  const route = dosageInstruction?.at(0)?.route?.coding?.at(0)?.display ?? "";

  const asNeeded = dosageInstruction?.at(0)?.asNeeded ? "As Needed" : undefined;

  return [
    startCase(status),
    medication?.display,
    value && unit ? `${value} ${unit}` : undefined,
    timing || undefined,
    asNeeded,
    route || undefined,
  ]
    .filter((e) => e)
    .join(" · ");
};

export function createReferenceV5(
  type: string,
  refId: string,
  display?: string,
  identifier?: Identifier,
) {
  const ref: ReferenceV5 = {
    reference: `${type}/${refId}`,
    type,
  };

  if (identifier) {
    ref.identifier = identifier;
  }

  if (display) {
    ref.display = display;
  }

  return ref;
}

export const getIdFromRefValue = (value: string) => value?.split("/")[1];

export const getResourceFromReference = (reference: string) =>
  reference?.split("/")[0];
